import React, { useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	Stack,
	TextField,
} from '@mui/material';
import type { AgentAction } from '../../Services/agents';

interface Props {
	open: boolean;
	action: AgentAction | null; // null = création
	existingIds: string[];
	onClose: () => void;
	onSave: (action: AgentAction) => void;
}

const SEVERITIES: AgentAction['severity'][] = ['info', 'warning', 'critical'];
const ID_RE = /^[a-z0-9_]{2,40}$/;

const empty = (): AgentAction => ({ id: '', name: '', description: '', params: {}, severity: 'warning', default_enabled: true });

// #region ActionDialog
// Le formulaire d'une action, dans une modale : la liste reste lisible, le formulaire
// a la place qu'il faut. L'identifiant ne s'édite qu'à la création : c'est une clé
// référencée par les réglages client et les plans.
export const ActionDialog: React.FC<Props> = ({ open, action, existingIds, onClose, onSave }) => {
	const creating = action === null;
	const [draft, setDraft] = useState<AgentAction>(empty());
	const [paramsText, setParamsText] = useState('{}');
	useEffect(() => {
		const a = action ?? empty();
		setDraft(a);
		setParamsText(JSON.stringify(a.params ?? {}, null, 2));
	}, [action, open]);

	const params = parseParams(paramsText);
	const idTaken = creating && existingIds.includes(draft.id);
	const idInvalid = !ID_RE.test(draft.id);
	const canSave = !idInvalid && !idTaken && draft.name.trim() !== '' && params !== null;
	const set = (patch: Partial<AgentAction>) => setDraft((d) => ({ ...d, ...patch }));

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{creating ? 'Nouvelle action' : `Modifier « ${action?.name} »`}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<Stack direction="row" spacing={2}>
						<TextField
							label="Identifiant"
							value={draft.id}
							onChange={(e) => set({ id: e.target.value })}
							disabled={!creating}
							error={creating && draft.id !== '' && (idInvalid || idTaken)}
							helperText={idTaken ? 'Déjà utilisé' : 'a-z, 0-9, _ ; figé après création'}
							sx={{ width: 260 }}
						/>
						<TextField label="Nom" value={draft.name} onChange={(e) => set({ name: e.target.value })} fullWidth />
					</Stack>
					<TextField
						label="Ce que l'agent surveille"
						helperText="La phrase que le client lira. Dire le seuil et ce qui déclenche l'alerte."
						value={draft.description}
						onChange={(e) => set({ description: e.target.value })}
						multiline
						minRows={3}
						fullWidth
					/>
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField select label="Sévérité" value={draft.severity} onChange={(e) => set({ severity: e.target.value as AgentAction['severity'] })} sx={{ width: 180 }}>
							{SEVERITIES.map((s) => (
								<MenuItem key={s} value={s}>
									{s}
								</MenuItem>
							))}
						</TextField>
						<FormControlLabel
							control={<Checkbox checked={draft.default_enabled} onChange={(e) => set({ default_enabled: e.target.checked })} />}
							label="Active par défaut chez un nouveau client"
						/>
					</Stack>
					<TextField
						label="Paramètres (JSON)"
						helperText={params === null ? 'JSON invalide' : 'Les seules clés que le client pourra ajuster, avec leur valeur par défaut.'}
						value={paramsText}
						onChange={(e) => setParamsText(e.target.value)}
						error={params === null}
						multiline
						minRows={3}
						fullWidth
						inputProps={{ style: { fontFamily: 'monospace' } }}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Annuler</Button>
				<Button variant="contained" disabled={!canSave} onClick={() => onSave({ ...draft, params: params ?? {} })}>
					{creating ? 'Ajouter' : 'Appliquer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
// #endregion

// #region parseParams
const parseParams = (text: string): Record<string, unknown> | null => {
	try {
		const v = JSON.parse(text);
		return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
	} catch {
		return null;
	}
};
// #endregion
